import {
  Box,
  Checkbox,
  FormControlLabel,
  FormControl,
  Typography,
  CheckboxProps,
  ClickAwayListener,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import CheckBoxOutlineBlankIcon from "@material-ui/icons/CheckBoxOutlineBlank";
import * as React from "react";

import { useDevice } from "@/hooks/useDevice";
import theme from "@/theme";

import { QuestionCircleIcon } from "./icon";

const useStyles = makeStyles(() => ({
  root: {
    display: "flex",
    flexDirection: "column",
    margin: theme.spacing(1),
  },
  checkbox: {
    "& .MuiSvgIcon-root": { fontSize: 20 },
    "&$checked": {
      color: theme.palette.primary.main,
    },
  },
  checked: {},
  error: {
    "&&&": { color: theme.palette.error.main },
    "&$checked": {
      color: theme.palette.error.main,
    },
  },
  disabled: {
    "&&&": { color: theme.palette.action.disabled },
  },
  label: {
    "&&": { color: theme.palette.text.primary, fontSize: 14, fontWeight: 400, lineHeight: 1.5 },
  },
  icon: {
    marginLeft: theme.spacing(1),
  },
  helperText: {
    marginLeft: theme.spacing(4.5),
  },
  infoRow: {
    marginLeft: theme.spacing(4.5),
  },
}));

interface CustomCheckboxProps extends CheckboxProps {
  label: string;
  helperText?: string;
  checked: boolean;
  onChange: (_event: React.ChangeEvent<HTMLInputElement>) => void;
  error?: boolean;
  disabled?: boolean;
  info?: string;
  alignTitleOnly?: boolean;
}

const CustomCheckboxRef: React.ForwardRefRenderFunction<HTMLInputElement, CustomCheckboxProps> = (
  { label, helperText = "", checked, onChange, error = false, disabled = false, info, alignTitleOnly = false },
  ref
) => {
  const classes = useStyles();
  const { isMobile } = useDevice();
  const [open, setOpen] = React.useState(false);

  const handleTooltipClose = () => {
    setOpen(false);
  };

  const handleTooltipOpen = () => {
    setOpen(true);
  };

  const renderMultilineTooltip = (text: string) => (
    <>
      {text.split("\n").map((line, i) => (
        <React.Fragment key={i}>
          {line}
          <br />
        </React.Fragment>
      ))}
    </>
  );

  const infoNode =
    info &&
    (isMobile ? (
      // モバイルではクリックで表示
      <ClickAwayListener onClickAway={handleTooltipClose}>
        <Tooltip
          PopperProps={{
            disablePortal: true,
          }}
          onClose={handleTooltipClose}
          open={open}
          disableFocusListener
          disableHoverListener
          disableTouchListener
          title={renderMultilineTooltip(info)}
        >
          <IconButton className={classes.icon} size="small" onClick={handleTooltipOpen}>
            <QuestionCircleIcon width="13.5" height="13.5" />
          </IconButton>
        </Tooltip>
      </ClickAwayListener>
    ) : (
      <Tooltip title={renderMultilineTooltip(info)}>
        <IconButton className={classes.icon} size="small">
          <QuestionCircleIcon width="13.5" height="13.5" />
        </IconButton>
      </Tooltip>
    ));

  if (alignTitleOnly) {
    return (
      <FormControl className={classes.root}>
        <FormControlLabel
          control={
            <Checkbox
              icon={error ? <CheckBoxOutlineBlankIcon className={classes.error} /> : <CheckBoxOutlineBlankIcon />}
              checkedIcon={<CheckBoxIcon />}
              checked={checked}
              onChange={onChange}
              disabled={disabled}
              inputRef={ref}
              classes={{
                root: classes.checkbox,
                checked: error ? classes.error : classes.checked,
                disabled: classes.disabled,
              }}
            />
          }
          label={
            <Typography variant="h4" className={`${classes.label} ${error ? classes.error : disabled ? classes.disabled : ""}`}>
              {label}
            </Typography>
          }
        />
        {helperText && (
          <Box className={classes.helperText}>
            <Typography variant="caption">{renderMultilineTooltip(helperText)}</Typography>
          </Box>
        )}
        {infoNode && <Box className={classes.infoRow}>{infoNode}</Box>}
      </FormControl>
    );
  }

  return (
    <FormControl className={classes.root}>
      <FormControlLabel
        control={
          <Checkbox
            icon={error ? <CheckBoxOutlineBlankIcon className={classes.error} /> : <CheckBoxOutlineBlankIcon />}
            checkedIcon={<CheckBoxIcon />}
            checked={checked}
            onChange={onChange}
            disabled={disabled}
            inputRef={ref}
            classes={{
              root: classes.checkbox,
              checked: error ? classes.error : classes.checked,
              disabled: classes.disabled,
            }}
          />
        }
        label={
          <>
            <Typography variant="h4" className={`${classes.label} ${error ? classes.error : disabled ? classes.disabled : ""}`}>
              {label}
            </Typography>
            {helperText && <Typography variant="caption">{renderMultilineTooltip(helperText)}</Typography>}
            {infoNode}
          </>
        }
      />
    </FormControl>
  );
};

const CustomCheckbox = React.forwardRef(CustomCheckboxRef);
export { CustomCheckbox };

// サンプルコンポーネントでチェックボックスを使用する
const CheckboxExample: React.FC = () => {
  const classes = useStyles();
  const [checked, setChecked] = React.useState(false);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChecked(event.target.checked);
  };

  return (
    <div className={classes.root}>
      <CustomCheckbox label="選択肢" checked={checked} onChange={handleChange} />
      <CustomCheckbox label="選択肢" checked={checked} onChange={handleChange} error />
      <CustomCheckbox label="選択肢" checked={true} onChange={handleChange} />
      <CustomCheckbox label="選択肢" checked={false} onChange={handleChange} disabled />
    </div>
  );
};

export default CheckboxExample;
